// A report's table as a real Excel table: a title, the headings on a dark band, borders, pesos and counts
// in their own number formats, a filter on the headings, the headings frozen, and a totals row in bold.
// exceljs, loaded on the click (`descargarTablas`), because SheetJS CE cannot write colours or borders —
// the same reason `cierreExcel.js` uses it for the Matriz.
//
// `libroDeTablas(ExcelJS, hojas)` is pure but for exceljs, so a test can write and read a real workbook.

const FORMATOS = {
  money: '"$"#,##0.00;[Red]-"$"#,##0.00',
  count: "#,##0",
  // B-136 — a fraction (0.077) drawn as 7.7 %.
  percent: "0.0%",
  date: "dd/mm/yyyy",
  text: "@",
};

const BORDE = { style: "thin", color: { argb: "FFD1D5DB" } };
const BORDES = { top: BORDE, left: BORDE, bottom: BORDE, right: BORDE };

/**
 * @param {object} ExcelJS
 * @param {{ nombre: string, titulo?: string, subtitulo?: string,
 *           columnas: { titulo: string, ancho?: number, formato?: "money"|"count"|"percent"|"date"|"text" }[],
 *           filas: any[][], totales?: any[] }[]} hojas
 */
export function libroDeTablas(ExcelJS, hojas) {
  const libro = new ExcelJS.Workbook();
  hojas.forEach((hoja) => {
    const ws = libro.addWorksheet(hoja.nombre.slice(0, 31));
    let fila = 1;
    if (hoja.titulo) {
      ws.getCell(fila, 1).value = hoja.titulo;
      ws.getCell(fila, 1).font = { bold: true, size: 14 };
      fila += 1;
    }
    if (hoja.subtitulo) {
      ws.getCell(fila, 1).value = hoja.subtitulo;
      ws.getCell(fila, 1).font = { color: { argb: "FF6B7280" }, size: 10 };
      fila += 1;
    }
    if (hoja.titulo || hoja.subtitulo) fila += 1;

    const encabezado = fila;
    hoja.columnas.forEach((columna, i) => {
      const celda = ws.getCell(encabezado, i + 1);
      celda.value = columna.titulo;
      celda.font = { bold: true, color: { argb: "FFFFFFFF" } };
      celda.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF111827" } };
      celda.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      celda.border = BORDES;
      ws.getColumn(i + 1).width = columna.ancho || 16;
    });
    ws.getRow(encabezado).height = 30;

    const escribir = (valores, negrita, fondo) => {
      fila += 1;
      valores.forEach((valor, i) => {
        const columna = hoja.columnas[i] || {};
        const celda = ws.getCell(fila, i + 1);
        celda.value = valor === undefined ? null : valor;
        if (columna.formato && FORMATOS[columna.formato]) celda.numFmt = FORMATOS[columna.formato];
        if (["money", "count", "percent"].includes(columna.formato)) celda.alignment = { horizontal: "right" };
        celda.border = BORDES;
        if (negrita) celda.font = { bold: true };
        if (fondo) celda.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fondo } };
      });
    };
    hoja.filas.forEach((valores, i) => escribir(valores, false, i % 2 ? "FFF9FAFB" : null));
    if (hoja.totales) escribir(hoja.totales, true, "FFE5E7EB");

    if (hoja.filas.length) {
      ws.autoFilter = { from: { row: encabezado, column: 1 }, to: { row: encabezado + hoja.filas.length, column: hoja.columnas.length } };
    }
    ws.views = [{ state: "frozen", ySplit: encabezado }];
  });
  return libro;
}

/** Build and save the workbook — exceljs and file-saver loaded on the click. */
export async function descargarTablas(nombreArchivo, hojas) {
  const [{ default: ExcelJS }, { saveAs }] = await Promise.all([import("exceljs"), import("file-saver")]);
  const buffer = await libroDeTablas(ExcelJS, hojas).xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), nombreArchivo);
}
